const {
  ModalTest: {
    default: Modal,
    actions: { toggleModal },
  },
  Button,
} = window.Edocat.Components;
const { List } = window.Material;
import { last, range } from 'ramda';
import { getFile, getErrorIndices } from '../../../utils';
import { fileAdded, filesAdded, fileUpdated } from '../../../utils/activityStreams';
import UploadRow from './UploadRow';
import { Dispatch } from 'redux';
import { ReduxComponentInterface } from 'edocatbase/Global';

import { uploadFile, uploadNewVersion, updateActivity } from '../../../actions';

type NewVersionData = {
  nodeRef: string;
  fileName: string;
  majorVersion: boolean;
  desc: string;
};

interface UploadModalProps extends ReduxComponentInterface {
  disallowedExtensions: string[];
  parentNodeRef: string;
  site: string;
  path: string;
  items: {
    files: any[];
  };
  name: string;
  callback: Function;
  uploadAction: Function;
  isOpen: boolean;
}

interface UploadModalState {
  files: any[];
  uploading: boolean;
}

class UploadModal extends window.React.PureComponent<UploadModalProps, UploadModalState> {
  timeout = 0;

  constructor(props: UploadModalProps) {
    super(props);
    this.state = {
      files: [],
      uploading: false,
    };
  }

  componentDidMount() {
    const { items } = this.props;
    if (items) {
      const newFiles = items.files;
      if (newFiles.length > 0) {
        this.setState(
          () => ({
            files: newFiles.map(file => ({
              file,
              excluded: false,
              done: false,
              error: '',
              progress: -1,
            })),
          }),
          () => this.onUpload(true, range(0, newFiles.length))
        );
      }
    }
  }

  onUpload = (fileExistCheck: boolean, indices: number[], newVersionData?: NewVersionData) => {
    this.setState(
      state => ({
        files: state.files.map((item, index) =>
          indices.includes(index) ? { ...item, excluded: false, done: false, error: '' } : item
        ),
        uploading: true,
      }),
      async () => {
        for (let i = 0; i < indices.length; i++) {
          await this.processFile(indices[i], indices.length, fileExistCheck, newVersionData);
        }

        this.setState({
          uploading: false,
        });

        if (this.state.files.every(item => item.done)) {
          this.timeout = window.setTimeout(this.onModalClose, 3000);
        }
      }
    );
  };

  processFile = async (i: number, length: number, fileExistCheck: boolean, newVersionData?: NewVersionData) => {
    const { files } = this.state;
    const { site, path, disallowedExtensions, parentNodeRef, uploadAction } = this.props;

    const item = files[i];

    let status = 'done';
    let value: boolean | string = true;

    if (disallowedExtensions?.includes(last(item.file.name.split('.')))) {
      status = 'excluded';
      value = true;
    } else {
      const filePath = item?.file?.fullPath ? item?.file?.fullPath.replace(item?.file?.name ?? '', '') : '';
      const fullPath = path + filePath;
      const resolvedFile = item.file?.file ? await getFile(item?.file) : item?.file;

      const onProgress = (event: any) => {
        this.setState(prevState => ({
          files: [
            ...prevState.files.slice(0, i),
            {
              ...prevState.files[i],
              progress: Math.ceil((event.loaded / event.total) * 100),
            },
            ...prevState.files.slice(i + 1),
          ],
        }));
      };

      try {
        if (uploadAction) {
          await uploadAction(resolvedFile, onProgress);
        } else {
          let response;

          if (newVersionData) {
            response = await uploadNewVersion(
              resolvedFile,
              newVersionData.nodeRef,
              newVersionData.majorVersion,
              newVersionData.desc,
              onProgress
            );
          } else {
            response = await uploadFile(resolvedFile, site, fullPath, fileExistCheck, onProgress);
          }

          parentNodeRef && length > 4 && i + 1 === length && updateActivity(filesAdded(length, parentNodeRef));

          response?.nodeRef &&
            length < 5 &&
            updateActivity(
              newVersionData
                ? fileUpdated(newVersionData.fileName, newVersionData.nodeRef)
                : fileAdded(response.fileName, response.nodeRef)
            );
        }

        status = 'done';
        value = true;
      } catch (e) {
        status = 'error';
        value = e || 'error';
      }
    }

    this.setState(state => ({
      files: [...state.files.slice(0, i), { ...item, [status]: value }, ...state.files.slice(i + 1)],
    }));
  };

  onModalClose = () => {
    const { actions, name, callback } = this.props;

    if (this.timeout) {
      window.clearTimeout(this.timeout);

      this.timeout = 0;
    }

    actions.toggleModal(false, name);

    callback && callback();
  };

  render() {
    const { files, uploading } = this.state;
    const { name } = this.props;
    return (
      <Modal
        name={name}
        title={window.Alfresco.messages.global['edocat.doc-lib.uploadNewDocs']}
        onClose={this.onModalClose}
        disableBackdropClick={uploading}
        disableEscapeKeyDown={uploading}
      >
        <div className="modal-body">
          <List>
            {files.map((item, index) => (
              <UploadRow key={`uploadModalRow${index}`} item={item} index={index} onUpload={this.onUpload} />
            ))}
          </List>
        </div>
        <div className="modal-actions">
          {!uploading && files.some(item => item.error && item.error.includes('file exists')) && (
            <Button
              label={window.Alfresco.messages.global['edocat.upload.rename-all']}
              onClick={() => this.onUpload(false, getErrorIndices(files))}
            />
          )}
          <Button
            disabled={uploading}
            label={window.Alfresco.messages.global['button.close']}
            onClick={this.onModalClose}
          />
        </div>
      </Modal>
    );
  }
}

const mapStateToProps = (state: any, props: UploadModalProps) => ({
  items: state.modal[props.name]?.items,
  isOpen: state.modal[props.name]?.isOpen,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: window.redux.bindActionCreators({ toggleModal }, dispatch),
});

export default window.reactRedux.connect(mapStateToProps, mapDispatchToProps)(UploadModal);
